import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Users, Plus, Mail, Search, Trash2, MapPin, Coins } from "lucide-react";
import { EmployeeStatistics } from "./employee-management/EmployeeStatistics";
import { CSVImport } from "./employee-management/CSVImport";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
  maximum_points_per_employee?: number;
}

interface Employee {
  id: string;
  invitation_status: string;
  invited_at: string;
  accepted_at: string | null;
  first_name?: string;
  last_name?: string;
  email?: string;
  position?: string;
  residence_country?: string;
  current_country?: string;
  profiles?: {
    first_name: string;
    last_name: string;
    email: string;
    position: string | null;
  } | null;
  remote_work_requests?: Array<{
    id: string;
    start_date: string;
    end_date: string;
    status: string;
    destination_country: string;
  }>;
  employee_points?: Array<{
    total_points: number;
    used_points: number;
    month: number;
    year: number;
  }>;
}

interface EmployeesTabProps {
  company: Company | null;
}

const EmployeesTab = ({ company }: EmployeesTabProps) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    firstName: "",
    lastName: "",
    email: "",
    position: "",
    residenceCountry: "",
    nestPoints: "10"
  });
  const { toast } = useToast();

  useEffect(() => {
    if (company) {
      fetchEmployees();
    }
  }, [company]);

  useEffect(() => {
    const filtered = employees.filter(employee => {
      const displayName = employee.first_name && employee.last_name 
        ? `${employee.first_name} ${employee.last_name}`
        : employee.profiles 
        ? `${employee.profiles.first_name} ${employee.profiles.last_name}`
        : '';
      
      const displayEmail = employee.email || 
        (employee.profiles ? employee.profiles.email : '');
      
      const displayPosition = employee.position || 
        (employee.profiles ? employee.profiles.position : '') || '';

      return (
        displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        displayEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        displayPosition.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    setFilteredEmployees(filtered);
  }, [employees, searchTerm]);

  const fetchEmployees = async () => {
    if (!company) return;
    
    try {
      // First, fetch employees
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          invitation_status,
          invited_at,
          accepted_at,
          first_name,
          last_name,
          email,
          position,
          residence_country,
          profiles (
            first_name,
            last_name,
            email,
            position
          ),
          employee_points (
            total_points,
            used_points,
            month,
            year
          )
        `)
        .eq('company_id', company.id)
        .order('invited_at', { ascending: false });

      if (error) throw error;

      // Then fetch remote work requests separately using secure function
      const { data: remoteWorkRequests } = await supabase
        .rpc('get_company_remote_work_requests');
      
      // Process employees to determine current country and latest points
      const processedEmployees = (data || []).map(employee => {
        // Filter requests for this employee
        const employeeRequests = (remoteWorkRequests || []).filter(
          req => req.employee_id === employee.id
        );

        const approvedRemoteWork = employeeRequests.filter(
          req => req.status === 'approved'
        );
        
        const currentRemoteWork = approvedRemoteWork.find(req => {
          const startDate = new Date(req.start_date);
          const endDate = new Date(req.end_date);
          const today = new Date();
          return startDate <= today && endDate >= today;
        });
        
        // Get the latest employee_points record
        const latestPoints = employee.employee_points?.reduce((latest, current) => {
          if (!latest) return current;
          
          const latestDate = new Date(latest.year, latest.month - 1);
          const currentDate = new Date(current.year, current.month - 1);
          
          return currentDate > latestDate ? current : latest;
        }, null);
        
        return {
          ...employee,
          remote_work_requests: employeeRequests,
          current_country: currentRemoteWork?.destination_country || employee.residence_country,
          employee_points: latestPoints ? [latestPoints] : []
        };
      });
      
      setEmployees(processedEmployees);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les employés"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.firstName || !newEmployee.lastName || !newEmployee.email) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires"
      });
      return;
    }

    const points = parseInt(newEmployee.nestPoints) || 10;
    if (points < 0) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Le nombre de points doit être positif"
      });
      return;
    }

    try {
      // Insert employee
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .insert({
          company_id: company?.id,
          first_name: newEmployee.firstName,
          last_name: newEmployee.lastName,
          email: newEmployee.email,
          position: newEmployee.position || null,
          residence_country: newEmployee.residenceCountry || null,
          invitation_status: 'invitation_non_envoyee'
        })
        .select('id')
        .single();

      if (employeeError) throw employeeError;

      // Create employee points record for current month
      const now = new Date();
      const { error: pointsError } = await supabase
        .from('employee_points')
        .insert({
          employee_id: employeeData.id,
          month: now.getMonth() + 1,
          year: now.getFullYear(),
          total_points: points,
          used_points: 0
        });

      if (pointsError) throw pointsError;

      toast({
        title: "Employé ajouté",
        description: `${newEmployee.firstName} ${newEmployee.lastName} a été ajouté avec ${points} Nest Points`
      });

      setIsDialogOpen(false);
      setNewEmployee({ firstName: "", lastName: "", email: "", position: "", residenceCountry: "", nestPoints: "10" });
      fetchEmployees();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'ajouter l'employé"
      });
    }
  };

  const handleSendInvitation = async (employeeId: string, email: string) => {
    try {
      const { error } = await supabase
        .from('employees')
        .update({ invitation_status: 'invitation_envoyee' })
        .eq('id', employeeId);

      if (error) throw error;

      toast({
        title: "Invitation envoyée",
        description: `L'invitation a été envoyée à ${email}`
      });

      fetchEmployees();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'envoyer l'invitation"
      });
    }
  };

  const handleDeleteEmployee = async (employeeId: string, name: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${name} ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', employeeId);

      if (error) throw error;

      toast({
        title: "Employé supprimé",
        description: `${name} a été supprimé de l'entreprise`
      });

      fetchEmployees();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de supprimer l'employé"
      });
    }
  };

  const getStatusBadge = (status: string, acceptedAt: string | null) => {
    if (acceptedAt) {
      return <Badge className="bg-success text-success-foreground">Actif</Badge>;
    }
    
    switch (status) {
      case 'invitation_non_envoyee':
        return <Badge variant="outline">Non invité</Badge>;
      case 'invitation_envoyee':
        return <Badge variant="secondary">Invitation envoyée</Badge>;
      case 'pending':
        return <Badge variant="secondary">En attente</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Section */}
      <EmployeeStatistics 
        employees={employees} 
        company={company}
      />

      {/* Employee Management Card */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestion des collaborateurs
              </CardTitle>
              <CardDescription>
                Gérez les invitations et l'accès de vos {employees.length} employés
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <CSVImport 
                company={company}
                onImportComplete={fetchEmployees}
              />
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un salarié
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Ajouter un nouvel employé</DialogTitle>
                    <DialogDescription>
                      Ajoutez un collaborateur à votre entreprise
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">Prénom *</Label>
                        <Input
                          id="firstName"
                          value={newEmployee.firstName}
                          onChange={(e) => setNewEmployee(prev => ({...prev, firstName: e.target.value}))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Nom *</Label>
                        <Input
                          id="lastName"
                          value={newEmployee.lastName}
                          onChange={(e) => setNewEmployee(prev => ({...prev, lastName: e.target.value}))}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newEmployee.email}
                        onChange={(e) => setNewEmployee(prev => ({...prev, email: e.target.value}))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="position">Poste</Label>
                      <Input
                        id="position"
                        value={newEmployee.position}
                        onChange={(e) => setNewEmployee(prev => ({...prev, position: e.target.value}))}
                        placeholder="Développeur, Manager, etc."
                      />
                    </div>
                    <div>
                      <Label htmlFor="residenceCountry">Pays de résidence</Label>
                      <Input
                        id="residenceCountry"
                        value={newEmployee.residenceCountry}
                        onChange={(e) => setNewEmployee(prev => ({...prev, residenceCountry: e.target.value}))}
                        placeholder="France, Espagne, etc."
                      />
                    </div>
                    <div>
                      <Label htmlFor="nestPoints">Nest Points à attribuer</Label>
                      <Input
                        id="nestPoints"
                        type="number"
                        min="0"
                        value={newEmployee.nestPoints}
                        onChange={(e) => setNewEmployee(prev => ({...prev, nestPoints: e.target.value}))}
                        placeholder="10"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Points mensuels pour les demandes de télétravail
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button onClick={handleAddEmployee}>
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 mt-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un employé..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        
        <CardContent>
          {filteredEmployees.length === 0 ? (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {employees.length === 0 ? "Aucun employé pour le moment" : "Aucun employé ne correspond à votre recherche"}
              </p>
              {employees.length === 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  Commencez par inviter vos premiers employés
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead>Pays résidence</TableHead>
                  <TableHead>Pays actuel</TableHead>
                  <TableHead>Nest Points</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((employee) => {
                  const displayName = employee.first_name && employee.last_name 
                    ? `${employee.first_name} ${employee.last_name}`
                    : employee.profiles 
                    ? `${employee.profiles.first_name} ${employee.profiles.last_name}`
                    : 'Nom non défini';
                  
                  const displayEmail = employee.email || 
                    (employee.profiles ? employee.profiles.email : 'Email non défini');
                  
                  const displayPosition = employee.position || 
                    (employee.profiles ? employee.profiles.position : null) || '-';
                  
                  // Get points info
                  const employeePoints = employee.employee_points?.[0];
                  const availablePoints = employeePoints 
                    ? employeePoints.total_points - employeePoints.used_points 
                    : 0;
                  
                  return (
                    <TableRow key={employee.id}>
                      <TableCell className="font-medium">{displayName}</TableCell>
                      <TableCell>{displayEmail}</TableCell>
                      <TableCell>{displayPosition}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">{employee.residence_country || 'Non renseigné'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 text-primary" />
                          <span className="text-sm font-medium">
                            {employee.current_country || employee.residence_country || 'Non renseigné'}
                          </span>
                          {employee.current_country && employee.current_country !== employee.residence_country && (
                            <Badge variant="outline" className="text-xs">
                              Télétravail
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                       <TableCell>
                        <div className="flex items-center gap-2">
                          <Coins className="h-3 w-3 text-warning" />
                          <span className="text-sm font-medium">
                            {availablePoints}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            /{company?.maximum_points_per_employee || 0}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(employee.invitation_status, employee.accepted_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {employee.invitation_status === 'invitation_non_envoyee' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleSendInvitation(employee.id, displayEmail)}
                            >
                              <Mail className="h-3 w-3 mr-1" />
                              Inviter
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteEmployee(employee.id, displayName)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeesTab;